import React, { useEffect, useRef, useState, useMemo } from "react";

/** Lib Imports */
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { FileUpload } from "primereact/fileupload";
import MolForm, { Field } from "@moltech/molui/molform";
import withPopupForm from "@moltech/molui/withPopupForm";
import Toast from "@moltech/molui/toast";
import PropTypes from "prop-types";
import PhoneField from "../../components/common/PhoneInput";
import { useDependentData } from "../../app/hooks/useDependentData";
import { useMasterData } from "../../app/hooks/useMasterData";

/** Utilities */
import { validateEmail, validatePhone } from "../../utils/validators/FieldsValidators";

/** ENUM */
import UserRoles from "../../enums/UserRoles.ENUM";
import { ApplicantStatus } from "../../enums/ApplicantStatus.ENUM";

/** RTK Query */
import { usePostDataMutation } from "../../app/services/commonApi";
import { usePostFileDataMutation } from "../../app/services/fileUploadApi";

/** CSS Import */
import './Applicants.scss'
import { DependentDataCacheURL, MasterDataCacheURL } from "../../enums/CacheURL";

const AddApplicants = ({ applicantData, registerFooterButtons, setTitle, onHide, onCallback, }) => {

    const formRef = useRef();
    const [defaultValue, setDefaultValue] = useState();

    const [saveData, { isLoading: isSavingData }] = usePostDataMutation();
    const [uploadFile, { isLoading: isUploading }] = usePostFileDataMutation();
    const { data: triggerGetJobPosition } = useMasterData(MasterDataCacheURL.GetPublishedJobOpening);

    const { data: interviewerData, fetchData: fetchInterviewers } = useDependentData(DependentDataCacheURL.GetAllUsersByRoleId, UserRoles.HR);

    const [resumeFile, setResumeFile] = useState(null);

    const [applicantFormData, setApplicantFormData] = useState(defaultValue || {});
    const [selectedChannel, setSelectedChannel] = useState(null);
    const { data: triggerGetChannels } = useMasterData(MasterDataCacheURL.GetAllChannels);
    const { data: sourceData, fetchData: fetchSources } = useDependentData(DependentDataCacheURL.GetAllSourceByChannelId, selectedChannel, { skip: !selectedChannel });
    const today = new Date();

    const channelOptions = useMemo(() => {
        if (!Array.isArray(triggerGetChannels)) return [];

        return triggerGetChannels.map(item => ({
            label: item.channelName,
            value: item.channelId
        }));
    }, [triggerGetChannels]);

    useEffect(() => {
        if (selectedChannel) {
            fetchSources();
        }
    }, [selectedChannel]);

    const sourceName = useMemo(() => {
        if (!Array.isArray(sourceData)) return [];

        return sourceData.map((item) => ({
            label: item.sourceName,
            value: item.sourceId,
        }));
    }, [sourceData]);


    const handleChannelChange = (e) => {
        const formData = formRef.current?.getData();

        setSelectedChannel(e.value);

        setApplicantFormData((prev) => ({
            ...formData,
            sourceId: null,
            channelId: e.value
        }));
    };

    const handleSourceChange = (e) => {
        const formData = formRef.current?.getData();

        setApplicantFormData((prev) => ({
            ...formData,
            sourceId: e.value
        }));
    };

    useEffect(() => {
        fetchInterviewers();
    }, []);
    const interviewerOptions = useMemo(() => {
        if (!Array.isArray(interviewerData)) return [];

        return interviewerData.map((item) => ({
            label: `${item.firstName} ${item.lastName}`,
            value: item.userId
        }));
    }, [interviewerData]);

    const jobPosition = useMemo(() => {
        if (!Array.isArray(triggerGetJobPosition)) return [];

        return triggerGetJobPosition.map(item => ({
            label: item.jobOpeningName,
            value: item.jobOpeningId
        }));
    }, [triggerGetJobPosition]);

    useEffect(() => {
        setDefaultValue({});
        setTitle(`Add Candidate`);
        if (applicantData) {
            setTitle(`Update Candidate`);
            setDefaultValue({
                applicantId: applicantData?.applicantId,
                firstName: applicantData?.firstName,
                lastName: applicantData?.lastName,
                gender: applicantData?.gender,
                applicantEmail: applicantData?.applicantEmail,
                applicantPhone: applicantData?.applicantPhone,
                jobOpeningId: applicantData?.jobOpeningId,
                interviewerId: applicantData?.interviewerId,
                applicantStatusId: applicantData?.applicantStatusId,
                responsibleUser: applicantData?.responsibleUser
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicantData, interviewerOptions]);

    const onFormSubmit = async () => {
        const formData = formRef.current?.getData();
        if (!formData) return;

        let currentResumeId = applicantData?.applicantResumeId || null;

        // Step 1: Upload Resume if selected
        if (resumeFile) {
            const fileData = new FormData();
            fileData.append("File", resumeFile);
            fileData.append("ApplicantId", applicantData?.applicantId || 0);

            try {
                const uploadRes = await uploadFile({
                    url: '/ManageApplicants/UploadApplicantResume',
                    data: fileData
                }).unwrap();

                if (uploadRes?.success) {
                    currentResumeId = uploadRes.data;
                } else {
                    Toast.error(uploadRes?.message || "File upload failed");
                    return;
                }
            } catch (error) {
                console.error("Resume upload error:", error);
                Toast.error("Error uploading file");
                return;
            }
        }

        // Step 2: Submit Applicant Data as JSON
        const dataToSend = {
            ApplicantId: applicantData?.applicantId || 0,
            FirstName: formData.firstName,
            LastName: formData.lastName,
            Gender: formData.gender || "",
            ApplicantEmail: formData.applicantEmail,
            ApplicantPhone: formData.applicantPhone,
            JobOpeningId: formData.jobOpeningId,
            PositionApplied: formData.jobOpeningId,
            ApplicantStatusId: ApplicantStatus.OPEN,
            ResponsibleUser: formData.responsibleUser,
            ApplyDate: formData.applyDate ? new Date(formData.applyDate).toISOString() : null,
            Comments: formData.comments || "",
            ApplicantResumeId: currentResumeId,
            channelId: formData.channelId,
            sourceId: formData.sourceId,
        };

        const apiCall = applicantData?.applicantId
            ? saveData({ url: '/ManageApplicants/UpdateApplicant', data: dataToSend })
            : saveData({ url: '/ManageApplicants/AddApplicant', data: dataToSend });

        apiCall.then((res) => {
            if (res.error?.message) {
                Toast.warn(res.error.message);
            } else if (res.data?.success) {
                onCallback?.({ ...res.data });
                onHide?.();
            }
        });
    };

    const onResumeSelect = (event) => {
        const file = event.files[0];
        setResumeFile(file);
        Toast.success("Resume selected");
    };

    const onResumeClear = () => {
        setResumeFile(null);
    };

    const onSave = () => {
        if (formRef.current) {
            formRef.current.submit();
        }
    };

    useEffect(() => {
        registerFooterButtons(
            <div className="bottom-btn-sec">
                <Button label="Close" icon="pi pi-times" className=" btn secondary-light-btn    p-button-sm p-button " onClick={onHide} />
                <Button label="Save" icon="pi pi-check" className="btn secondary-btn   p-button-sm p-button " onClick={onSave} disabled={isUploading || isSavingData} loading={isUploading || isSavingData} />
            </div>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUploading, isSavingData]);

    return (
        <div className="p-fluid">
            <div className='add-applicant-form'>
                <MolForm ref={formRef} onSubmit={onFormSubmit} initialValues={applicantFormData}>
                    <Field
                        component={InputText}
                        name="firstName"
                        label="First Name"
                        placeholder="First Name"
                        validation='required'
                        required
                        minLength={1} maxLength={50}
                    />
                    <Field
                        component={InputText}
                        name="lastName"
                        label="Last Name"
                        placeholder="Last Name"
                        validation='required'
                        required
                        minLength={1} maxLength={50}
                    />
                    <Field
                        component={Dropdown}
                        name="gender"
                        label="Gender"
                        placeholder="Select Gender"
                        options={[
                            { label: 'Male', value: 'Male' },
                            { label: 'Female', value: 'Female' },
                            { label: 'Other', value: 'Other' }
                        ]}
                        showClear
                    />
                    <Field
                        component={InputText}
                        name="applicantEmail"
                        label="Email"
                        placeholder="Email"
                        validation={validateEmail}
                        required
                    />
                    <Field
                        component={PhoneField}
                        name="applicantPhone"
                        label="Phone"
                        placeholder="Phone"
                        validation={validatePhone}
                        //mask="99999 99999? x 99999"
                        required
                    />
                    <Field
                        component={Dropdown}
                        name="jobOpeningId"
                        label="Job Position"
                        placeholder="Job Position"
                        options={jobPosition}
                        filter
                        showClear
                        showFilterClear={true}
                        optionLabel="label"
                        optionValue="value"
                    />
                    <Field
                        component={Dropdown}
                        options={interviewerOptions}
                        name="responsibleUser"
                        label="Responsible HR"
                        placeholder="Select Responsible HR"
                        filter
                        showClear
                        showFilterClear={true}
                        optionLabel="label"
                        optionValue="value"
                        display="chip"
                        validation="required"
                        required
                    />
                    <Field
                        component={Calendar}
                        name="applyDate"
                        label="Apply Date"
                        placeholder="Select Apply Date"
                        dateFormat="dd-mm-yy"
                        showTime
                        hourFormat="12"
                        showIcon
                        showButtonBar
                        validation="required"
                        required
                        maxDate={today}
                    />
                    <Field
                        component={Dropdown}
                        name="channelId"
                        label="Channel"
                        options={channelOptions}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Channel"
                        validation="required"
                        required
                        onChange={handleChannelChange}
                    />

                    <Field
                        component={Dropdown}
                        name="sourceId"
                        label="Source"
                        options={sourceName}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Source"
                        validation="required"
                        required
                        onChange={handleSourceChange}
                    />
                    <Field
                        component={FileUpload}
                        name="resume"
                        label={applicantData?.resumeFileName ? "Replace Resume" : "Resume Upload"}
                        mode="advanced"
                        accept=".pdf,.doc,.docx"
                        maxFileSize={5000000}
                        customUpload
                        onSelect={onResumeSelect}
                        onClear={onResumeClear}
                        onRemove={onResumeClear}
                        auto={false}
                        chooseLabel={applicantData?.resumeFileName ? "Select New Resume" : "Upload Resume"}
                        multiple={false}
                        emptyTemplate={<p className="m-0">Drag and drop resume here to upload.</p>}
                        showUploadButton={false}
                        showCancelButton={false}

                    />

                </MolForm>
            </div>
        </div>
    );
}

AddApplicants.propTypes = {
    applicantData: PropTypes.shape({
        applicantId: PropTypes.number,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        gender: PropTypes.string,
        applicantEmail: PropTypes.string,
        applicantPhone: PropTypes.string,
        jobOpeningId: PropTypes.number,
        interviewerId: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.arrayOf(PropTypes.number)
        ]),
        applicantStatusId: PropTypes.number,
        responsibleUser: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.arrayOf(PropTypes.number)
        ]),
        applicantResumeId: PropTypes.number,
        resumeFileName: PropTypes.string
    }),
    registerFooterButtons: PropTypes.func.isRequired,
    setTitle: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
    onCallback: PropTypes.func,
};

export default withPopupForm(AddApplicants);